import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Badge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { ArrowsClockwiseIcon, TruckIcon, MapPinIcon, WarningIcon, GasPumpIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import FuelGauge from "@/components/FuelGauge";
import FuelUpsPanel from "@/components/FuelUpsPanel";
import FuelReceiptViewer from "@/components/FuelReceiptViewer";
import MotiveSyncReportModal from "@/components/MotiveSyncReportModal";
import { detectRegion, formatVolume, formatCurrency, formatPricePerUnit, unitSystemFromTruck } from "@/lib/units";

// Custom icon (default leaflet icons break in CRA bundlers)
const truckIcon = new L.DivIcon({
  className: "truck-marker",
  html: '<div style="background:#2563EB;width:34px;height:34px;border-radius:4px;display:flex;align-items:center;justify-content:center;color:white;font-weight:900;font-family:Work Sans;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.25)">🚛</div>',
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

const offlineTruckIcon = new L.DivIcon({
  className: "truck-marker-offline",
  html: '<div style="background:#94A3B8;width:34px;height:34px;border-radius:4px;display:flex;align-items:center;justify-content:center;color:white;font-weight:900;font-family:Work Sans;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.25)">🚛</div>',
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

// Small drop-pin for fill-up events. Uploaded = emerald, pending = amber.
const fuelPinIcon = (uploaded) => new L.DivIcon({
  className: "fuel-pin",
  html: `<div style="background:${uploaded ? "#059669" : "#F59E0B"};width:22px;height:22px;border-radius:11px 11px 11px 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;color:white;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.35)"><span style="transform:rotate(45deg);font-size:11px">⛽</span></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

// Cheapest-contract-stop pin — green with a dollar sign.
const dealPinIcon = new L.DivIcon({
  className: "deal-pin",
  html: `<div style="background:#16A34A;width:26px;height:26px;border-radius:13px 13px 13px 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;color:white;border:2px solid white;box-shadow:0 2px 5px rgba(0,0,0,0.35)"><span style="transform:rotate(45deg);font-size:13px;font-weight:900">$</span></div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

// Low-fuel truck marker — red with a yellow halo so dispatch spots it instantly.
const LOW_FUEL_PCT = 20;
const lowFuelTruckIcon = new L.DivIcon({
  className: "truck-marker-lowfuel",
  html: '<div style="background:#DC2626;width:34px;height:34px;border-radius:4px;display:flex;align-items:center;justify-content:center;color:white;font-weight:900;font-family:Work Sans;border:2px solid #FDE047;box-shadow:0 0 0 4px rgba(220,38,38,0.30),0 2px 6px rgba(0,0,0,0.25)">🚛</div>',
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});
const isLowFuel = (t) => t.fuel_percentage != null && t.fuel_percentage < LOW_FUEL_PCT;

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 9);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [points, map]);
  return null;
}

export default function FleetMap() {
  const nav = useNavigate();
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncReport, setSyncReport] = useState(null);
  const [fuelPins, setFuelPins] = useState([]);
  const [showPins, setShowPins] = useState(true);
  const [pinDays, setPinDays] = useState(365);
  const [pinDate, setPinDate] = useState(new Date().toLocaleDateString("en-CA")); // YYYY-MM-DD, local day
  const [receiptEvent, setReceiptEvent] = useState(null);
  const [showDeals, setShowDeals] = useState(false);
  const [deals, setDeals] = useState([]);
  const [showLowOnly, setShowLowOnly] = useState(false);
  // FuelUpsPanel tracks its own receipt-viewer state internally; it pings us
  // when it opens/closes so we can hide the Leaflet map (canvas paints above
  // the dialog overlay otherwise).
  const [panelViewerOpen, setPanelViewerOpen] = useState(false);
  const anyViewerOpen = !!receiptEvent || panelViewerOpen;
  const pollRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/trucks");
    setTrucks(data);
    setLoading(false);
  };

  const loadPins = async (days = pinDays) => {
    try {
      const { data } = await api.get(`/fuel/map-pins?days=${days}`);
      setFuelPins(data.pins || []);
    } catch { /* ignore */ }
  };

  const loadDeals = async () => {
    try {
      const { data } = await api.get("/fuel/cheapest-near-trucks");
      setDeals(data.stops || []);
    } catch { /* ignore */ }
  };

  const refresh = async (silent = false) => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/integrations/motive/refresh-locations");
      if (!silent) {
        if (data.ok) toast.success(`Updated ${data.updated} truck location(s)`);
        else toast.error(`Refresh failed: ${data.reason}`);
      }
      await Promise.all([load(), loadPins()]);
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setRefreshing(false);
    }
  };

  const syncDrivers = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/motive/sync-drivers");
      setSyncReport(data);   // pop the report modal so admin can see matched/unmatched
      if (data.linked_now > 0) {
        toast.success(`Linked ${data.linked_now} new driver(s) to Motive`);
      } else if (data.unmatched > 0) {
        toast.info(`No new links. ${data.unmatched} driver(s) still need email fixes.`);
      } else {
        toast.success("All drivers already linked");
      }
      await load(); // Refresh truck list so driver_name updates
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    load();
    loadPins();
    // Auto-refresh every 5 minutes
    pollRef.current = setInterval(() => refresh(true), 5 * 60 * 1000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadPins(pinDays); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [pinDays]);

  useEffect(() => { if (showDeals) loadDeals(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showDeals]);

  const located = trucks.filter((t) => t.latitude != null && t.longitude != null);
  const lowFuelTrucks = located.filter(isLowFuel);
  const visibleTrucks = showLowOnly ? lowFuelTrucks : located;
  const linkedNoLoc = trucks.filter((t) => t.motive_vehicle_id && (t.latitude == null || t.longitude == null));
  const unlinked = trucks.filter((t) => !t.motive_vehicle_id);

  const center = located.length > 0
    ? [located[0].latitude, located[0].longitude]
    : [39.8283, -98.5795]; // US center

  // Fill-up pins are shown for a single selected calendar day (local time).
  const dayPins = fuelPins.filter(
    (p) => p.recorded_at && new Date(p.recorded_at).toLocaleDateString("en-CA") === pinDate
  );

  // Cheapest contract stops — dedupe by state (one pin per state) and list the units near it.
  const dealStops = React.useMemo(() => {
    const m = new Map();
    for (const d of deals) {
      if (!d.stop || d.stop.lat == null || d.stop.lng == null) continue;
      if (!m.has(d.state)) m.set(d.state, { ...d.stop, units: [] });
      if (d.unit_number) m.get(d.state).units.push(d.unit_number);
    }
    return [...m.values()];
  }, [deals]);

  return (
    <div data-testid="fleet-map-page">
      <PageHeader eyebrow="Live tracking" title="Fleet Map" subtitle="Real-time GPS positions from Motive · auto-refresh every 5 minutes.">
        <span className="font-mono text-xs text-slate-500">
          {located.length}/{trucks.length} located
        </span>
        <button
          type="button"
          onClick={() => setShowPins((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-sm text-xs font-bold border ${showPins ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-slate-300 text-slate-600"}`}
          data-testid="map-toggle-pins"
        >
          <GasPumpIcon size={14} weight="bold" />
          Fill-up pins {showPins && `(${dayPins.length})`}
        </button>
        <input
          type="date"
          value={pinDate}
          max={new Date().toLocaleDateString("en-CA")}
          onChange={(e) => setPinDate(e.target.value)}
          className="h-9 px-2 rounded-sm text-xs font-mono border border-slate-300 text-slate-700 bg-white"
          data-testid="map-pin-date"
          title="Show fill-ups from this day"
        />
        <button
          type="button"
          onClick={() => setShowDeals((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-sm text-xs font-bold border ${showDeals ? "bg-green-600 border-green-700 text-white" : "bg-white border-slate-300 text-slate-600"}`}
          data-testid="map-toggle-deals"
          title="Show the cheapest contract diesel stop in each truck's state"
        >
          <GasPumpIcon size={14} weight="bold" />
          Cheapest fuel {showDeals && `(${dealStops.length})`}
        </button>
        <button
          type="button"
          onClick={() => setShowLowOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-sm text-xs font-bold border ${showLowOnly ? "bg-red-600 border-red-700 text-white" : lowFuelTrucks.length > 0 ? "bg-red-50 border-red-300 text-red-700" : "bg-white border-slate-300 text-slate-600"}`}
          data-testid="map-toggle-lowfuel"
          title={`Trucks under ${LOW_FUEL_PCT}% fuel`}
        >
          <WarningIcon size={14} weight="fill" />
          Low fuel ({lowFuelTrucks.length})
        </button>
        <Button onClick={syncDrivers} disabled={syncing} variant="outline" className="rounded-sm" data-testid="map-sync-drivers-btn">
          <ArrowsClockwiseIcon size={14} weight="bold" className={`mr-2 ${syncing ? "animate-spin" : ""}`} /> Sync drivers
        </Button>
        <Button onClick={() => refresh(false)} disabled={refreshing} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="map-refresh-button">
          <ArrowsClockwiseIcon size={14} weight="bold" className={`mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </PageHeader>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map — hidden while a receipt viewer is open so it doesn't bleed
            through the Radix overlay. Use `visibility: hidden` so Leaflet
            keeps its layout and doesn't need re-initialization. */}
        <div
          className="lg:col-span-2 bg-white border border-slate-300 rounded-sm overflow-hidden"
          data-testid="map-container"
          style={{ visibility: anyViewerOpen ? "hidden" : "visible" }}
        >
          <div style={{ height: "65vh", minHeight: 500 }}>
            {loading ? (
              <div className="h-full flex items-center justify-center font-mono text-sm text-slate-500">LOADING MAP...</div>
            ) : (
              <MapContainer center={center} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds points={visibleTrucks.map((t) => ({ lat: t.latitude, lng: t.longitude }))} />
                {visibleTrucks.map((t) => (
                  <Marker key={t.id} position={[t.latitude, t.longitude]} icon={isLowFuel(t) ? lowFuelTruckIcon : truckIcon}>
                    <Popup>
                      <div className="font-display font-bold text-sm">Unit {t.unit_number}{t.location_hold && <span className="ml-1.5 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-amber-100 text-amber-800 border border-amber-200">📍 Held</span>}</div>
                      {isLowFuel(t) && (
                        <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-red-100 text-red-700 border border-red-200" data-testid={`popup-lowfuel-${t.unit_number}`}>
                          <WarningIcon size={11} weight="fill" /> Low fuel · {Math.round(t.fuel_percentage)}%
                        </div>
                      )}
                      <div className="text-xs text-slate-700">{t.year} {t.make} {t.model}</div>
                      {t.driver_name && (
                        <div className="text-xs text-slate-800 font-bold mt-1">
                          👤 {t.driver_name}
                          {t.driver_source === "motive" && <span className="text-emerald-600 font-mono text-[10px] ml-1">· live</span>}
                        </div>
                      )}
                      {t.location_address && <div className="text-xs text-slate-500 mt-1">{t.location_address}</div>}
                      {t.current_speed_mph != null && (
                        <div className="text-xs font-mono mt-1">Speed: {Number(t.current_speed_mph).toFixed(0)} mph</div>
                      )}
                      {t.fuel_percentage != null && (
                        <div className="mt-2"><FuelGauge percentage={t.fuel_percentage} size="sm" updatedAt={t.fuel_updated_at} /></div>
                      )}
                      <div className="text-[10px] text-slate-400 font-mono mt-1">
                        {t.location_updated_at && new Date(t.location_updated_at).toLocaleString()}
                      </div>
                      <Link to={`/trucks/${t.id}`} className="text-blue-600 text-xs underline mt-2 block">Open truck →</Link>
                    </Popup>
                  </Marker>
                ))}
                {/* Fill-up pins */}
                {showPins && dayPins.map((p) => {
                  const uploaded = p.receipt_status === "uploaded";
                  const gallons = p.driver_reported_gallons ?? p.estimated_gallons;
                  const when = p.recorded_at ? new Date(p.recorded_at) : null;
                  // Volume follows TRUCK (Canadian truck → L); currency follows LOCATION.
                  const pinTruck = trucks.find((tr) => tr.id === p.truck_id);
                  const unitRegion = unitSystemFromTruck(pinTruck);
                  const currencyRegion = detectRegion({ location_address: p.location_address });
                  // Per-unit rate from the driver's reported fill (cost ÷ volume).
                  // Display in the truck's unit: $/gal (US) or $/L (Canadian).
                  const dispVol = unitRegion === "CA" && gallons != null ? gallons * 3.78541 : gallons;
                  const perUnit = (p.driver_reported_cost != null && dispVol) ? p.driver_reported_cost / dispVol : null;
                  const volLabel = unitRegion === "CA" ? "L" : "gal";
                  return (
                    <Marker key={`pin-${p.id}`} position={[p.latitude, p.longitude]} icon={fuelPinIcon(uploaded)}>
                      <Popup>
                        <div className="section-eyebrow mb-1 text-emerald-700 font-mono">/// Fill-up {unitRegion === "CA" && "🇨🇦"}</div>
                        <div className="font-display font-bold text-sm">Truck #{p.truck_unit_number}</div>
                        {p.driver_name && <div className="text-xs text-slate-700 font-mono uppercase">👤 {p.driver_name}</div>}
                        {p.location_address && <div className="text-xs text-slate-500 mt-1">{p.location_address}</div>}
                        <div className="text-xs font-mono mt-1">
                          {Math.round(p.before_pct || 0)}% → {Math.round(p.after_pct || 0)}%
                          {gallons != null && <span className="ml-1 text-slate-700 font-bold">· {formatVolume(gallons, unitRegion, 1)}</span>}
                        </div>
                        {p.driver_reported_cost != null && (
                          <div className="text-xs font-mono mt-0.5 font-bold text-emerald-700">
                            {formatCurrency(p.driver_reported_cost, currencyRegion)}
                            {perUnit != null && (
                              <span className="ml-1 font-normal text-slate-600" data-testid={`fuel-pin-rate-${p.id}`}>
                                ({formatCurrency(perUnit, currencyRegion)}/{volLabel})
                              </span>
                            )}
                          </div>
                        )}
                        {p.sheet_rate != null && (
                          <div className="text-[11px] font-mono mt-0.5 text-slate-600" data-testid={`fuel-pin-sheet-${p.id}`}>
                            Pump rate today: <span className="font-bold text-slate-900">{formatPricePerUnit(p.sheet_rate, currencyRegion, 3)}</span>
                            {p.sheet_rate_vendor && <span className="text-slate-400"> · {p.sheet_rate_vendor}{p.sheet_rate_site ? ` #${p.sheet_rate_site}` : ""}</span>}
                          </div>
                        )}
                        {when && <div className="text-[10px] text-slate-400 font-mono mt-1">{when.toLocaleString()}</div>}
                        {uploaded && (
                          <button
                            type="button"
                            onClick={() => setReceiptEvent({ ...p, unit_number: p.truck_unit_number, actual_gallons: p.driver_reported_gallons, cost: p.driver_reported_cost })}
                            className="text-blue-600 text-xs underline mt-2 block"
                          >
                            View receipt →
                          </button>
                        )}
                      </Popup>
                    </Marker>
                  );
                })}
                {/* Cheapest contract-stop pins (one per state) */}
                {showDeals && dealStops.map((s, i) => (
                  <Marker key={`deal-${s.state}-${i}`} position={[s.lat, s.lng]} icon={dealPinIcon}>
                    <Popup>
                      <div className="section-eyebrow mb-1 text-green-700 font-mono">/// Cheapest diesel · {s.state}</div>
                      <div className="font-display font-bold text-sm">{s.name || s.vendor || "Contract stop"}{s.site ? ` #${s.site}` : ""}</div>
                      <div className="text-xs text-slate-600">{s.city}, {s.state}{s.vendor ? ` · ${s.vendor}` : ""}</div>
                      <div className="text-sm font-mono font-bold text-green-700 mt-1" data-testid={`deal-price-${s.state}`}>${Number(s.price).toFixed(3)}/gal</div>
                      {s.units?.length > 0 && (
                        <div className="text-[11px] text-slate-500 mt-1">Near unit{s.units.length > 1 ? "s" : ""}: {s.units.filter(Boolean).join(", ")}</div>
                      )}
                      <div className="text-[10px] text-slate-400 mt-1">City-level location · best contract price in {s.state}</div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            )}
          </div>
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          {trucks.every((t) => !t.motive_vehicle_id) && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="no-motive-banner">
              <div className="flex items-start gap-2">
                <WarningIcon size={20} weight="fill" className="text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-display font-bold mb-1">No trucks linked to Motive</div>
                  <p className="text-slate-700 text-xs leading-relaxed">
                    Open any truck to link it to a Motive vehicle, or configure your Motive token in <Link to="/integrations" className="text-blue-700 underline">Integrations</Link>.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-300 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="section-eyebrow mb-0.5">/// Live</div>
                <h3 className="font-display text-base font-bold tracking-tight">{showLowOnly ? "Low-fuel trucks" : "Located trucks"}</h3>
              </div>
              {lowFuelTrucks.length > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-bold uppercase bg-red-100 text-red-700 border border-red-200" data-testid="lowfuel-count-badge">
                  <WarningIcon size={11} weight="fill" /> {lowFuelTrucks.length} low
                </span>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto scrollbar-thin">
              {visibleTrucks.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-500 font-mono">{showLowOnly ? "No trucks under 20% fuel" : "No live positions"}</div>
              ) : (
                visibleTrucks.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => nav(`/trucks/${t.id}`)}
                    className="w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors duration-150"
                    data-testid={`map-truck-${t.unit_number}`}
                  >
                    <div className="flex items-center gap-2">
                      <TruckIcon size={14} weight="bold" className={`flex-shrink-0 ${isLowFuel(t) ? "text-red-600" : "text-blue-600"}`} />
                      <span className="font-mono font-bold text-sm">{t.unit_number}</span>
                      {isLowFuel(t) && (
                        <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wide bg-red-100 text-red-700 border border-red-200 flex-shrink-0" title={`Under ${LOW_FUEL_PCT}% fuel`}>⛽ Low</span>
                      )}
                      {t.location_hold && (
                        <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200 flex-shrink-0" title="Location manually held">📍 Held</span>
                      )}
                      <span className="text-xs text-slate-600 truncate">{t.make} {t.model}</span>
                    </div>
                    {t.driver_name && (
                      <div className="text-[11px] text-slate-700 font-mono uppercase tracking-wide mt-0.5 truncate" title={t.driver_source === "motive" ? "Signed into Motive tablet" : "Assigned in Rep Fleet"}>
                        👤 {t.driver_name}
                        {t.driver_source === "motive" && <span className="text-emerald-600 ml-1 normal-case">· live</span>}
                      </div>
                    )}
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                      {t.location_address || `${t.latitude.toFixed(4)}, ${t.longitude.toFixed(4)}`}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      {t.fuel_percentage != null && <FuelGauge percentage={t.fuel_percentage} size="sm" />}
                      {t.location_updated_at && (
                        <div className="text-[10px] text-slate-400 font-mono">{new Date(t.location_updated_at).toLocaleTimeString()}</div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {linkedNoLoc.length > 0 && (
            <div className="bg-white border border-slate-300 rounded-sm">
              <div className="px-4 py-3 border-b border-slate-200">
                <div className="section-eyebrow mb-0.5">/// Linked, no signal</div>
                <h3 className="font-display text-base font-bold tracking-tight">Awaiting GPS</h3>
              </div>
              <div className="p-3">
                {linkedNoLoc.map((t) => (
                  <div key={t.id} className="text-xs text-slate-700 py-1 font-mono">{t.unit_number} · {t.make} {t.model}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fuel-ups feed spans the full width below the map */}
      <div className="mt-6">
        <FuelUpsPanel onViewerChange={setPanelViewerOpen} />
      </div>

      {/* Fill-up pin receipt viewer */}
      <FuelReceiptViewer open={!!receiptEvent} event={receiptEvent} onClose={() => setReceiptEvent(null)} />

      {/* Bulk driver sync result modal */}
      <MotiveSyncReportModal open={!!syncReport} report={syncReport} onClose={() => setSyncReport(null)} />
    </div>
  );
}
